import React from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  Shield,
  Users,
  Home,
  ArrowRight,
  Star,
  Clock,
  Phone,
} from "lucide-react";
import { facilitiesData } from "../data/facilities";

const HomePage: React.FC = () => {
  console.log("HomePage");
  const features = [
    {
      icon: Heart,
      title: "Compassionate Care",
      description:
        "Our residents receive a home feeling and atmosphere which honors their privacy and dignity, along with hands-on assistance to keep them safe in a comfortable ambiance.",
    },
    {
      icon: Shield,
      title: "Safety First",
      description:
        "Our facilities are built with a safety-first, resident-focused approach in every aspect of daily life—from the physical environment to meals, activities, and personal care. We provide secure, familiar surroundings designed to prevent falls, reduce confusion, and promote comfort.",
    },
    {
      icon: Users,
      title: "Community Living",
      description:
        "At Almond Tree Senior Care, we qualify our residents and family members to ensure we are aligned in values and commitments. We are a choice for families who are committed to a positive living experience and value their loved one's continued health restoration.",
    },
    {
      icon: Home,
      title: "Home-like Environment",
      description:
        "We are a place dedicated to serving residents by offering a warm and supportive home where they can thrive with the care they need. When living alone is no longer the safest option, we proudly become their next best place to home—comfortable, uplifting, and full of compassion.",
    },
  ];

  const testimonials = [
    {
      name: "Leslie Olive",
      relation: "Spouse of Resident",
      text: "I am so very, very blessed to of found this wonderful, amazing home for my husband who has Alzheimers. Sonia is the owner who is so very, very loving and caring!! Her staff is amazing!! My husband is treated with love and so much respect. His home is also a very, very safe place to be! All his needs are so lovingly attended to. Calls are always made to me if they have even the tinest concern. Communication with Sonia and her staff is always loving and respectful and that is how they treat my husband also. I can sleep at night knowing he is loved, and so very cared for. I know that Sonia and the staff are dedicated to each and every resident. All of Sonia's homes are spotless and are happy, healthy, calm places to be! Thank you Sonia and her staff for all of your concern, love and joy brought to my husband and I through some difficult times, and making our lives so much easier. Love to you all and bless you!! My husband and I both are very, very blessed....",
      rating: 5,
    },
    {
      name: "Nicholas Ulrich",
      relation: "Physical Therapist",
      text: "As a physical therapist, I've worked with patients in homes managed by Sonia for 6+ years. I cannot say enough positive words about her character, leadership and care provided by Almond Tree Senior Care. I believe this model senior care is by far the best and Almond Tree has perfected the model. They provide incredible service to all their residence.",
      rating: 5,
    },
    {
      name: "William Bryant",
      relation: "Son of Resident",
      text: "Almond Tree Senior Care has been looking after my mother and I can't say enough positive things about the care they are providing.  They assist her with her physical therapy every day, make sure she gets her exercises in and and so very attentive to her needs. Thank you Sonia!",
      rating: 5,
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img src="/transparent-logo.jpg" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Exceptional Care for Your <br />
              <span className="text-primary-beige">Loved Ones</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Experience peace of mind with our premium assisted living
              communities. Four beautiful locations offering compassionate care,
              comfortable living, and vibrant community life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/facilities#facilities"
                className="inline-flex items-center px-8 py-4 bg-primary-green text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                Tour Our Facilities
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <a
                href="tel:+17209997773"
                className="inline-flex items-center px-8 py-4 border-2 border-blue-600 text-primary-green font-semibold rounded-lg hover:bg-primary-green hover:text-white transition-all duration-300 transform hover:scale-105"
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Now: (720) 999-7773
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white bg-gradient-to-br from-blue-50 to-green-50 ">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Almond Tree?
            </h2>
            <p className="text-xl text-gray-600 max-w-1xl mx-auto">
              The almond tree is a sacred and ancient symbol, its blossom
              representing love, bravery, courage, purity, and hope. For Almond
              Tree Senior Care, it is the source of inspiration. We are an
              all-encompassing senior residence facility, and our primary
              mission and focus is the well-being of our residents. It is our
              sole purpose. Led by and together with our owner, Ms. Sonia
              Idnani, we embrace and demonstrate an approach that is central to
              our commitment. <br />
              Simply said, Almond Tree Senior Care is 'life with purpose'.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6 group-hover:bg-blue-200 transition-colors duration-300">
                  <feature.icon className="h-8 w-8 text-primary-green" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Facilities Preview */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Four Premier Facilities
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Each location offers unique amenities and specialized care
              tailored to meet diverse needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {facilitiesData.map((facility: any) => (
              <div
                key={facility.id}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <img
                  src={facility.images[0].src}
                  alt={facility.name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {facility.name}
                  </h3>
                  <p className="text-gray-600 mb-3">{facility.location}</p>
                  <div className="flex justify-between items-center text-sm text-gray-500 mb-4">
                    <span>{facility.beds} beds</span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                      {facility.specialty}
                    </span>
                  </div>
                  <Link
                    // to={`/facilities/${facility.id}`}
                    to={`/facilities#facilities`}
                    className="inline-flex items-center text-primary-green hover:text-blue-800 font-medium"
                  >
                    Learn More
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/facilities#facilities"
              className="inline-flex items-center px-6 py-3 bg-blue-700 text-white font-semibold rounded-lg hover:bg-primary-green transition-colors duration-300"
            >
              View All Facilities
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white" id="testimonials">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Families Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it. Hear from the families who trust
              us with their loved ones.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 relative">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-5 w-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "{testimonial.text}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">
                    {testimonial.name}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {testimonial.relation}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Learn More?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Schedule a tour of our facilities and see why families choose Almond
            Tree for their loved ones.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact#contactform"
              className="inline-flex items-center px-8 py-4 bg-white text-primary-green font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-300"
            >
              Schedule a Tour
              <Clock className="ml-2 h-5 w-5" />
            </Link>
            <a
              href="tel:+17209997773"
              className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-green transition-colors duration-300"
            >
              <Phone className="mr-2 h-5 w-5" />
              Call (720) 999-7773
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
